import { useState, useEffect } from 'react';
import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData, useRouteError, isRouteErrorResponse } from "@remix-run/react";
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { HandThumbUpIcon, HandThumbDownIcon, LinkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import {  ChevronDownIcon } from '@heroicons/react/20/solid';

import { getRelatedReposKNN } from '../server/related-knn.server';
import UpdatedAt from '../components/updatedAt';
import Description from '../components/description';
import { ExternalLinksList } from '../components/external_links_list';
import { RepoResult } from '../components/repo-list-item';
import { RemixRepoList } from '../components/remix-repo-list';
import { showThankYouToast, getModalElement } from '../components/utils';
import { apiCacheControlHeader, getPushedAtFormatted, getRelativeTimeString } from '../server/utils.server';
import {selectOffers} from '../server/offers.server';
import ButtonGroup from '../components/button-group';
import { HistoryType, addToHistory } from "../client/local-history.client";

const offerPositions = [5,15,25,35];

export const meta: MetaFunction = ({ data, params }: { data: any, params: any }) => {
  // Average repo name length: ~ 14.5 characters
  // SELECT avg(length(repo_name)) FROM gh_meta WHERE repo_name IS NOT NULL;

  // Title tag: 50-60 characters optimal, max 70 characters
  // Meta description: 140-160 characters optimal, max 170 characters

  const titleText = 'alternatives and similar packages'
  const titleLong = `${params.repoOwner}/${params.repoName} ${titleText}`;
  const titleShort = `${params.repoName} ${titleText}`;
  const title = titleLong.length <= 60 ? titleLong : titleShort;

  // `data` could be undefined in case of not found repo. Was causing 500 errors instead of 404
  const top6Results = (data?.relatedResults || []).slice(0, 6).map(relatedResult => relatedResult.repo_name.split('/')[1]);
  let description = `Alternatives to ${params.repoName}: ${params.repoName} vs ${top6Results[0]}. ${top6Results[1]} vs ${top6Results[2]}`;
  const descriptionAdditional = `. ${top6Results[3]} vs ${top6Results[4]}`;
  if (description.length + descriptionAdditional.length <= 160) {
    description = description + descriptionAdditional;
  }

  const canonicalURL = `${__BASE_URL__}/gh/${params.repoOwner}/${params.repoName}`;

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonicalURL,
    "relatedLink": `https://github.com/${params.repoOwner}/${params.repoName}`
  };

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonicalURL },
    { "script:ld+json": jsonLD },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalURL },
    { property: "og:image", content: __OG_IMAGE__ }
  ];
};

const selectOptionsDict = {
  'Most Relevant': 'relevanceScore',
  'Most Stars': 'stargazerCount',
  'Recently Updated': 'pushedAtNumericValue'
}

const feedbackKey = 'rr-feedback';

function getFeedbackKeyRepoName(loaderData: any): string {
  return `${loaderData.repoOwner}/${loaderData.repoName}`.toLowerCase();
}

function getFeedbackObj() {
  return JSON.parse(localStorage.getItem(feedbackKey) ?? '{}');
}

function isMoreThanOneDayOld(timestamp: number): boolean {
  const oneDay = 24 * 60 * 60 * 1000; // One day in milliseconds
  const currentTime = Date.now();
  return (currentTime - timestamp) > oneDay;
}

function showExtLinksModal(e) {
  getModalElement('modal_ext_links').showModal();
}

function showBadgeModal(e) {
  getModalElement('modal_badge').showModal();
}

export async function loader({
  params,
}: LoaderFunctionArgs) {
  const knnResponse = await getRelatedReposKNN(`${params.repoOwner}/${params.repoName}`);
  const offerLinks = selectOffers(knnResponse.repo_id, offerPositions.length);
  const offerMap = offerLinks.reduce((acc, currentOfferLink, idx) => {
    return Object.assign(acc, {[offerPositions[idx]]: currentOfferLink});
  }, {});
  // console.log(offerLinks)

  return json({
    repoOwner: params.repoOwner,
    repoName: params.repoName,
    repo_id: knnResponse.repo_id,
    description: knnResponse.description,
    pushedAt: knnResponse.pushedAt,
    updatedLabel: getRelativeTimeString(knnResponse.pushedAt),
    pushedAtFormatted: getPushedAtFormatted(knnResponse.pushedAt),
    stargazerCount: knnResponse.stargazerCount,
    relatedResults: knnResponse.results,
    offerMap
  },
    { headers: apiCacheControlHeader });
}

export default function Index() {
  const loaderData: any = useLoaderData();
  const starCount: number = loaderData.stargazerCount ?? loaderData.watchCount ?? 0;

  const [feedbackRecieved, setFeedbackRecieved] = useState<boolean>(false);
  const [selectedSortOption, setSelectedSortOption] = useState('Most Relevant');
  const selectedSortKey = selectOptionsDict[selectedSortOption];

  // make a copy of the array to avoid sorting mutation
  const sortedResults: RepoResult[] = [...loaderData.relatedResults].sort((a, b) => {
    return b[selectedSortKey] - a[selectedSortKey];
  });

  const handleButtonGroupChange = (buttonOption: string) => {
    setSelectedSortOption(buttonOption);
    // TODO: persist state in URL query param? list reverts when clicking back button
  };

  const rrLink = `https://relatedrepos.com/gh/${loaderData.repoOwner}/${loaderData.repoName}`;
  const markdownSource = `[![RelatedRepos](https://img.shields.io/badge/related-repos-yellow)](${rrLink})`;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(markdownSource);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    // clean up old items on component mount
    const feedbackObj = getFeedbackObj();
    for (const repoFullName of Object.keys(feedbackObj)) {
      const timeStamp = feedbackObj[repoFullName].timeStamp;
      if (isMoreThanOneDayOld(timeStamp)) {
        delete feedbackObj[repoFullName];
      }
    }
    localStorage.setItem(feedbackKey, JSON.stringify(feedbackObj));
  }, []);

  useEffect(() => {
    const feedbackObj = getFeedbackObj();
    setFeedbackRecieved(feedbackObj.hasOwnProperty(getFeedbackKeyRepoName(loaderData)));
    addToHistory(HistoryType.RepoVisit, `${loaderData.repoOwner}/${loaderData.repoName}`)
  }, [loaderData]); // loaderData dependency in case of page navigation

  const setFeedbackLocalStorage = (repoFullName: string, positiveFeedback: boolean) => {
    const feedbackObj = getFeedbackObj();
    // set new data
    feedbackObj[repoFullName] = {
      timeStamp: Date.now(),
      positiveFeedback
    }
    localStorage.setItem(feedbackKey, JSON.stringify(feedbackObj));
    setFeedbackRecieved(true);
  };

  const handleFeedbackClick = async (positiveFeedback: boolean) => {
    const url = `/api/feedback/gh/${loaderData.repoOwner}/${loaderData.repoName}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        repoOwner: loaderData.repoOwner,
        repoName: loaderData.repoName,
        positiveFeedback
      })
    });

    setFeedbackLocalStorage(getFeedbackKeyRepoName(loaderData), positiveFeedback);
    showThankYouToast();
  };

  // const starHistEmbedURL = `https://api.star-history.com/svg?repos=${top5RepoNames.join(',')}`;
  // const startHistStyles = {
  //   width: '100%',
  //   height: 'auto',
  //   minWidth: '600px', // Note the camelCase for min-width
  //   minHeight: '600px' // Note the camelCase for min-height
  // };

  return (
    <>
      <section className="background-gray px-2 py-2 border border-gray-100 rounded-md">

        <dialog id="modal_ext_links" className="modal">
          {/* https://daisyui.com/components/modal/?lang=en */}
          <div className="modal-box">
            <h3 className="text-lg font-bold py-2">{loaderData.repoOwner}/{loaderData.repoName} <span className="text-base font-normal text-rr-medium">external links</span></h3>
            <hr />

            <ExternalLinksList repoName={`${loaderData.repoOwner}/${loaderData.repoName}`} />

            <div className="modal-action">
              <form method="dialog">
                {/* if there is a button in form, it will close the modal */}
                <button className="rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-white/5 dark:hover:bg-white/20">Close</button>
              </form>
            </div>
          </div>
        </dialog>

        <dialog id="modal_badge" className="modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold py-1">{loaderData.repoOwner}/{loaderData.repoName}</h3>
            <div className="py-3 flex items-center space-x-1">
              <span>Readme badge preview - </span>
              <a href={rrLink}>
                <img src='https://img.shields.io/badge/related-repos-yellow' width="90" height="20" alt='Badge preview' />
              </a>
            </div>
            <p className='py-2'>If you own this repo, copy the snippet below and add it to your README.md</p>
            <hr />

            <div className="flex flex-col items-center">
              <div className="w-full relative">
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 px-2 py-1 text-xs background-button-white ring-1 ring-inset ring-gray-300 rounded-md transition-colors"
                >
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
                <pre className="max-w-full overflow-auto [scrollbar-width:thin] p-2 text-sm background-button-white ring-1 ring-inset ring-gray-300 rounded-md">
                  <code>
                    {markdownSource}
                  </code>
                </pre>
              </div>
            </div>

            <div className="modal-action">
              <form method="dialog">
                {/* if there is a button in form, it will close the modal */}
                <button className="rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-white/5 dark:hover:bg-white/20">Close</button>
              </form>
            </div>
          </div>
        </dialog>

        <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between py-1">
          <span className="flex items-center space-x-1">
            <span className='text-xl font-bold'>{loaderData.repoOwner} / {loaderData.repoName}</span>
            <span className="tooltip flex items-center">
              {/* Tooltip fix - https://claude.ai/chat/09d6407a-f819-48cf-a66b-251e1e9e6db8 */}
              <span className="tooltip-content">View on GitHub</span>
              <a 
                href={`https://github.com/${loaderData.repoOwner}/${loaderData.repoName}`} 
                target="_blank"
                className="ml-1 inline-flex items-center" 
                aria-label={`View "${loaderData.repoOwner}/${loaderData.repoName}" on GitHub`}
              >
                <ArrowTopRightOnSquareIcon className="inline-block size-4 text-gray-400" />
              </a>
            </span>
          </span>

          {/* https://daisyui.com/components/dropdown/#dropdown-using-popover-api-and-anchor-positioning */}

          <button className="inline-flex justify-center gap-x-1.5 px-2 py-1 background-button-gray text-rr-button-white-primary text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300 rounded-md" {...{ popovertarget: "popover-1" } as any} style={{ anchorName: "--anchor-1" }  /* as React.CSSProperties */} >
            More
            <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400 dark:text-gray-500" />
          </button>

          <ul className="dropdown menu w-52 text-base rounded-box background-gray shadow-sm ring-1 ring-inset ring-gray-300 rounded-md"
            popover="auto" id="popover-1" style={{ positionAnchor: "--anchor-1" }}>
            <li>
              <a href="#" role="button" onClick={showExtLinksModal} className='active:bg-gray-50 dark:active:bg-gray-800'>
                <LinkIcon aria-hidden="true" className="size-4 text-rr-light" />
                <span>External links</span>
              </a>
            </li>
            <li>
              <a href="#" role="button" onClick={showBadgeModal} className='active:bg-gray-50 dark:active:bg-gray-800'>
                <ShieldCheckIcon aria-hidden="true" className="size-4 text-rr-light" />
                <span>Readme badge</span>
              </a>
            </li>
          </ul>

        </div>
        {(loaderData.description !== 'None' && loaderData.description !== 'nan') &&
          <div className="text-sm text-rr-medium py-1">
            <Description description={loaderData.description} maxLength={347} />
          </div>
        }
        <div className="text-sm text-rr-light">
          <span className="mr-2">☆{starCount.toLocaleString()}</span>
          <UpdatedAt updatedLabel={loaderData.updatedLabel} pushedAtFormatted={loaderData.pushedAtFormatted} />
        </div>
      </section>

      <h1 className="pt-3 text-lg font-semibold text-rr-medium">
        Alternatives and similar repositories for {loaderData.repoName}
      </h1>

      <p className="text-sm text-rr-light pl-1">
        Users that are interested in {loaderData.repoName} are comparing it to the libraries listed below. We may earn a commission when you buy through links labeled 'Ad' on this page.
      </p>

      <div className='py-2 pl-1'>
        <div className='inline-block mr-1 text-sm text-rr-medium'>Sorting:</div>
        <div className='inline-block'>
          <ButtonGroup options={Object.keys(selectOptionsDict)} selectedOption={selectedSortOption} onSelect={handleButtonGroupChange} />
        </div>
      </div>

      <hr />

      {/* <iframe style={startHistStyles} src={starHistEmbedURL} frameBorder="0"></iframe> */}

      <RemixRepoList repoResults={sortedResults} offerMap={loaderData.offerMap} />

      {feedbackRecieved ? null :
        <div id="feedback-button" className="tooltip tooltip-left">
          <span className="tooltip-content">Are these results useful?</span>
          <div className="inline-flex items-center gap-x-1.5 background-button-white rounded-md ring-1 ring-inset ring-gray-300 px-3 py-2 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <span className="text-rr-button-white-primary hidden md:inline">Results:</span>
            <HandThumbUpIcon onClick={() => handleFeedbackClick(true)} className="h-5 w-5 text-rr-button-white-primary hover:cursor-pointer mr-1 md:mr-0" />
            <HandThumbDownIcon onClick={() => handleFeedbackClick(false)} className="h-5 w-5 text-rr-button-white-primary hover:cursor-pointer" />
          </div>
        </div>
      }

    </>
  );
}

export function ErrorBoundary() {
  const error: any = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div>
          <h1 className="text-xl font-bold mb-2">
            Repository not found
          </h1>

          <h2 className="flex items-center py-1">
            <span className="text-lg font-bold mr-1 ">
              {error.data}
            </span>
            <div className="tooltip flex items-center">
              <span className="tooltip-content">View on GitHub</span>
              <a href={`https://github.com/${error.data}`} target="_blank" className="ml-1 inline-flex items-center" aria-label={`View "${error.data}" on GitHub`}>
                <ArrowTopRightOnSquareIcon className="inline-block size-4 text-gray-400" />
              </a>
            </div>
          </h2>
          <p>This repository is not currently indexed.</p>
          <p>If it was created recently, it will be picked up during the next daily index update.</p>
          <p>Note: Only repositories with at least 15 stars are indexed.</p>
        </div>
      )
    }
    else {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          {/* only show error message in development */}
          <p>{process.env.NODE_ENV === "development" ? (error?.data ?? "Unknown error") : null}</p>
          {/* <p>{error.data}</p> */}
        </div>
      );
    }
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        {(process.env.NODE_ENV === "development" && error.stack) ? <>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </> : null}
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
